import { useQuery } from '@tanstack/react-query'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { Assessment, AssessmentsApiFilterKey } from '@omnea-app/assessments/assessmentsFrontendClient'
import { AssessmentsHttpClient } from '@omnea-app/assessments/assessmentsHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { isUuid, ValidUuidString } from '@omnea-app/packages/universal/string-utils/isUuid'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { AssessmentsTopLevelFilterKey } from '../utils/assessmentsTopLevelFilterKeys'

export const assessmentsQueryKey = 'assessments'

const mapFilterKeyToApiFilterKey = (
  topLevelFilterKey?: AssessmentsTopLevelFilterKey | ValidUuidString
): AssessmentsApiFilterKey => {
  switch (topLevelFilterKey) {
    case 'all':
    case 'complete':
    case 'rejected':
    case undefined:
      return 'all'
    case 'actionNeeded':
    case 'sent':
    case 'inProgress':
    case 'upcoming':
      return 'pending'
    default: {
      if (isUuid(topLevelFilterKey)) return 'all'
      assertNever(topLevelFilterKey)
      console.error(`Unhandled assessments top level filter: ${topLevelFilterKey}`)
      return 'pending'
    }
  }
}

const assessmentRecordsPerBatch = 100

const fetchAssessmentBatch = async (
  apiClient: AssessmentsHttpClient,
  params: { status: AssessmentsApiFilterKey; page: number; organisationSupplierId?: string }
) => {
  const { status, page, organisationSupplierId } = params
  const { data } = await apiClient.POST('/list', {
    body: { paginationModel: { page, pageSize: assessmentRecordsPerBatch }, status, organisationSupplierId },
  })
  return data
}

export const useListAssessments = (
  topLevelFilterKey?: AssessmentsTopLevelFilterKey | ValidUuidString,
  organisationSupplierId?: string
) => {
  const { getToken } = useAuthenticatedUser()
  const status = mapFilterKeyToApiFilterKey(topLevelFilterKey)

  return useQuery<Assessment[], Error>({
    queryKey: [assessmentsQueryKey, status, organisationSupplierId],
    queryFn: async () => {
      const authorization = await getToken()
      const assessmentsApi = new AssessmentsHttpClient({ authorization })

      const firstBatch = await fetchAssessmentBatch(assessmentsApi, { status, page: 0, organisationSupplierId })
      if (!firstBatch) return []
      const totalPages = Math.ceil(firstBatch.totalCount / assessmentRecordsPerBatch)
      if (totalPages < 2) {
        // The Assessment type has enum properties, whereas in the generated type from the API client, those are string unions as that
        // is what we actually receive via the API. Hence why we need to cast here.
        return firstBatch.assessments as Assessment[]
      }

      // All pages starting from index 1 (page 2)
      const pagesToLoad = range(1, totalPages)
      const restOfBatches = await Promise.all(
        pagesToLoad.map((page) => fetchAssessmentBatch(assessmentsApi, { status, page, organisationSupplierId }))
      )
      const allAssessments = [...firstBatch.assessments, ...restOfBatches.map((data) => data?.assessments).flat()].filter(
        Boolean
      ) as Assessment[]

      // remove duplicates between batches just in case
      return uniqBy(allAssessments, 'id')
    },
  })
}
